import { useCallback, useEffect, useMemo, useState, useReducer, RefObject } from 'react';
import Formatter from './formatter';
const reducer = (state, fields) => ({
  ...state,
  ...fields
});
const patternDefault = [];
export function useMaskedInput({
  alwaysShowMask = true,
  initialValue = '',
  inputMode,
  inputRef: ownRef,
  maskCharacter = '_',
  pattern = patternDefault
}) {
  const formatter = useMemo(() => {
    if (pattern?.length === 0) {
      return {
        value: initialValue,
        unmaskedValue: initialValue,
        keyDown: null,
        beforeInput: null,
        input: null,
        set: null
      };
    }
    return new Formatter({
      pattern,
      maskCharacter,
      refInput: ownRef,
      initialValue
    });
  }, [pattern, maskCharacter, initialValue, ownRef]);
  const [hasFocus, setHasFocus] = useState(alwaysShowMask);
  const [{
    maskedValue,
    unmaskedValue
  }, dispatch] = useReducer(reducer, {
    maskedValue: formatter.value,
    unmaskedValue: initialValue
  });
  useEffect(() => {
    dispatch({
      maskedValue: formatter.value,
      unmaskedValue: formatter.unmaskedValue
    });
  }, [pattern, maskCharacter, initialValue, formatter]);
  const handleKeyDown = useCallback(e => {
    if (formatter?.keyDown) {
      formatter.keyDown(e);
    }
  }, [formatter]);
  const handleBeforeInput = useCallback(e => {
    if (formatter?.beforeInput) {
      formatter.beforeInput(e);
    }
  }, [formatter]);
  const handleInput = useCallback(e => {
    if (pattern?.length === 0) {
      const val = e.target ? e.target.value : e;
      dispatch({
        maskedValue: val,
        unmaskedValue: val
      });
      return;
    }
    if (e.target && formatter.input) {
      const [newValue, unmasked] = formatter.input();
      dispatch({
        maskedValue: newValue,
        unmaskedValue: unmasked
      });
      return;
    }
    if (!e.target && formatter.set) {
      const [newValue, unmasked] = formatter.set(e);
      dispatch({
        maskedValue: newValue,
        unmaskedValue: unmasked
      });
    }
  }, [formatter, pattern]);
  const handleFocus = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => setHasFocus(true));
    }
  }, []);
  const handleBlur = useCallback(() => {
    setHasFocus(alwaysShowMask);
  }, [alwaysShowMask]);
  return {
    inputMode,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onInput: handleInput,
    onKeyDown: handleKeyDown,
    onBeforeInput: handleBeforeInput,
    value: hasFocus || unmaskedValue ? maskedValue : '',
    'data-unmasked-value': unmaskedValue
  };
}